/**
 * Amount parsing and formatting.
 *
 * Every amount in this product is an integer number of minor currency units. There is
 * no floating-point money arithmetic anywhere: parsing goes straight from the typed
 * string to an integer, and formatting goes straight from the integer to a string.
 */

import { currencyDigits, minorUnitFactor } from './currencies.js';

/** Largest accepted amount, in MAJOR units. Guards display and integer abuse. */
export const MAX_AMOUNT_MAJOR = 10_000_000;

export type ParseAmountError =
  'empty' | 'not_a_number' | 'negative' | 'zero' | 'too_large' | 'too_precise';

export type ParseAmountResult =
  { ok: true; minor: number } | { ok: false; error: ParseAmountError };

/**
 * Parse a user-typed amount into integer minor units.
 *
 * Accepts the shapes people actually type: `1200`, `1 200`, `1,200`, `1.200,50`,
 * `1,200.50`, `€300`, `300 €`. The separator that appears last and is followed by
 * 1–2 digits is treated as the decimal separator; everything else is a grouping mark.
 *
 * Rejects rather than rounds when the input carries more precision than the currency
 * supports — silently discarding a digit of someone's money is not acceptable, and for
 * a *maximum* it could round upward.
 *
 * One shape is genuinely ambiguous: a single separator before exactly three digits
 * ("300.500"). It is read as grouping, except in a 3-decimal currency where three
 * trailing digits are a real fraction. The UI echoes the parsed amount back under the
 * field before submission, so the person typing resolves the ambiguity rather than a
 * heuristic deciding silently.
 */
export function parseAmountToMinor(input: string, currency: string): ParseAmountResult {
  const digits = currencyDigits(currency);
  const factor = minorUnitFactor(currency);

  // Strip currency symbols, spaces (incl. NBSP / narrow NBSP) and stray letters.
  const cleaned = input
    .replace(/[\s\u00a0\u202f\u2007\u2009]/g, '')
    .replace(/[^\d.,\-+]/g, '')
    .trim();

  if (cleaned === '') return { ok: false, error: 'empty' };
  if (cleaned.startsWith('-')) return { ok: false, error: 'negative' };

  const unsigned = cleaned.startsWith('+') ? cleaned.slice(1) : cleaned;
  if (!/^[\d.,]+$/.test(unsigned)) return { ok: false, error: 'not_a_number' };

  const lastDot = unsigned.lastIndexOf('.');
  const lastComma = unsigned.lastIndexOf(',');
  const lastSep = Math.max(lastDot, lastComma);

  let intPart: string;
  let fracPart: string;

  if (lastSep === -1) {
    intPart = unsigned;
    fracPart = '';
  } else {
    const tail = unsigned.slice(lastSep + 1);
    // A trailing group of exactly 3 digits means grouping rather than decimals:
    // 1.200.000 / 1,200,000 / 1,200. Two exceptions:
    //  - more than one separator is always grouping;
    //  - in a 3-decimal currency (KWD, BHD) a single separator before 3 digits is a
    //    real decimal fraction, so it must not be swallowed as a grouping mark.
    const separatorCount = (unsigned.match(/[.,]/g) ?? []).length;
    const looksLikeGrouping =
      tail.length === 3 &&
      (separatorCount > 1 || (digits !== 3 && /^\d{1,3}$/.test(unsigned.slice(0, lastSep))));

    if (looksLikeGrouping) {
      intPart = unsigned;
      fracPart = '';
    } else {
      intPart = unsigned.slice(0, lastSep);
      fracPart = tail;
    }
  }

  intPart = intPart.replace(/[.,]/g, '');
  if (intPart === '') intPart = '0';

  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart)) {
    return { ok: false, error: 'not_a_number' };
  }

  // More decimals than the currency has minor units.
  if (fracPart.replace(/0+$/, '').length > digits) {
    return { ok: false, error: 'too_precise' };
  }

  const major = Number(intPart);
  if (!Number.isSafeInteger(major)) return { ok: false, error: 'too_large' };
  if (major > MAX_AMOUNT_MAJOR) return { ok: false, error: 'too_large' };

  const paddedFrac = (fracPart + '0'.repeat(digits)).slice(0, digits);
  const minor = major * factor + (digits > 0 ? Number(paddedFrac) : 0);

  if (!Number.isSafeInteger(minor)) return { ok: false, error: 'too_large' };
  if (minor <= 0) return { ok: false, error: 'zero' };

  return { ok: true, minor };
}

/** True when the value is a usable integer minor-unit amount. */
export function isValidMinorAmount(minor: unknown, currency: string): minor is number {
  if (typeof minor !== 'number' || !Number.isSafeInteger(minor)) return false;
  if (minor <= 0) return false;
  return minor <= MAX_AMOUNT_MAJOR * minorUnitFactor(currency);
}

/** Integer minor units -> major units as a number, for display only. */
export function minorToMajorNumber(minor: number, currency: string): number {
  return minor / minorUnitFactor(currency);
}

/**
 * Pin the space character `Intl` uses between a number and its currency symbol.
 *
 * French and Spanish formatting put a no-break space there, but which one depends on the
 * ICU version: older builds emit U+00A0, newer ones U+202F. Node and the browser can
 * disagree, and on a prerendered page that disagreement is a hydration mismatch on an
 * otherwise identical number. Normalising both to U+00A0 costs nothing visually and
 * removes the whole class of problem.
 */
function stableSpaces(formatted: string): string {
  return formatted.replace(/[\u00a0\u202f\u2009]/g, '\u00a0');
}

/**
 * Localised currency string, e.g. "€300" / "¥3,000" / "300,500 KD".
 * Falls back to a plain, still-correct rendering if `Intl` lacks the currency.
 */
export function formatMinor(minor: number, currency: string, locale = 'en'): string {
  const digits = currencyDigits(currency);
  const value = minorToMajorNumber(minor, currency);
  try {
    return stableSpaces(
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        // Whole amounts are the common case; don't force ".00" noise on them.
        ...(minor % minorUnitFactor(currency) === 0 ? { minimumFractionDigits: 0 } : {}),
      }).format(value),
    );
  } catch {
    return `${value.toFixed(digits)} ${currency}`;
  }
}

/** The bare number, formatted with grouping but no currency symbol. */
export function formatMinorNumber(minor: number, currency: string, locale = 'en'): string {
  const digits = currencyDigits(currency);
  const value = minorToMajorNumber(minor, currency);
  const fractionDigits = minor % minorUnitFactor(currency) === 0 ? 0 : digits;
  try {
    return stableSpaces(
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(value),
    );
  } catch {
    return value.toFixed(fractionDigits);
  }
}

/** The currency's symbol on its own, for the input prefix. */
export function currencySymbol(currency: string, locale = 'en'): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}
